import type { WorkflowStageKey, WorkflowStageStatus } from '@/lib/attune/workflow-status';

export type FirstCollectionStepKey =
  | 'collect_results'
  | 'read_pages'
  | 'build_report'
  | 'organize_insights'
  | 'prepare_workspace';

export type FirstCollectionStepState = 'complete' | 'active' | 'pending' | 'failed';

export interface FirstCollectionWorkflowStage {
  key: WorkflowStageKey;
  status: WorkflowStageStatus;
  required: boolean;
}

export interface FirstCollectionWorkflow {
  state: 'pending' | 'running' | 'completed' | 'failed';
  currentStage: WorkflowStageKey | null;
  stages: FirstCollectionWorkflowStage[];
  isComplete: boolean;
  hasFailed: boolean;
  progressPercent: number;
  error: string | null;
}

export interface FirstCollectionStep {
  key: FirstCollectionStepKey;
  label: string;
  state: FirstCollectionStepState;
}

export interface FirstCollectionProgress {
  steps: FirstCollectionStep[];
  activeStep: FirstCollectionStep;
  activeStepNumber: number;
  completedStepCount: number;
  progressPercent: number;
}

type FirstCollectionStepDefinition = {
  key: FirstCollectionStepKey;
  label: string;
  stageKeys: readonly WorkflowStageKey[];
};

export const FIRST_COLLECTION_STEP_DEFINITIONS: readonly FirstCollectionStepDefinition[] = [
  {
    key: 'collect_results',
    label: 'Collecting your first AI results',
    stageKeys: ['sampler'],
  },
  {
    key: 'read_pages',
    label: 'Reading the pages behind those results',
    stageKeys: ['scraper', 'brand_crawl'],
  },
  {
    key: 'build_report',
    label: 'Building your visibility report',
    stageKeys: ['citations_report'],
  },
  {
    key: 'organize_insights',
    label: 'Organizing mentions and sources',
    stageKeys: ['citations_indexer', 'citation_brand_analyzer'],
  },
  {
    key: 'prepare_workspace',
    label: 'Preparing your workspace',
    stageKeys: ['indexer', 'audit'],
  },
];

type StepResolution = {
  complete: boolean;
  failed: boolean;
};

function clampProgress(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function buildFirstCollectionProgress(
  workflow: FirstCollectionWorkflow,
): FirstCollectionProgress {
  const stageByKey = new Map(workflow.stages.map(stage => [stage.key, stage]));
  const resolutions: StepResolution[] = FIRST_COLLECTION_STEP_DEFINITIONS.map(definition => {
    const requiredStages = definition.stageKeys
      .map(stageKey => stageByKey.get(stageKey))
      .filter((stage): stage is FirstCollectionWorkflowStage =>
        Boolean(stage?.required && stage.status !== 'skipped'),
      );

    return {
      complete:
        workflow.isComplete ||
        (requiredStages.length > 0 && requiredStages.every(stage => stage.status === 'completed')),
      failed: requiredStages.some(stage => stage.status === 'failed'),
    };
  });

  const failedStepIndex = resolutions.findIndex(resolution => resolution.failed);
  const currentStageStepIndex = workflow.currentStage
    ? FIRST_COLLECTION_STEP_DEFINITIONS.findIndex(definition =>
      definition.stageKeys.includes(workflow.currentStage as WorkflowStageKey),
    )
    : -1;
  const firstIncompleteStepIndex = resolutions.findIndex(resolution => !resolution.complete);
  const activeStepIndex = workflow.isComplete
    ? FIRST_COLLECTION_STEP_DEFINITIONS.length - 1
    : failedStepIndex >= 0
      ? failedStepIndex
      : currentStageStepIndex >= 0 && !resolutions[currentStageStepIndex].complete
        ? currentStageStepIndex
        : Math.max(0, firstIncompleteStepIndex);

  const steps = FIRST_COLLECTION_STEP_DEFINITIONS.map((definition, index): FirstCollectionStep => {
    const resolution = resolutions[index];
    const state: FirstCollectionStepState = resolution.failed
      ? 'failed'
      : resolution.complete
        ? 'complete'
        : index === activeStepIndex
          ? 'active'
          : 'pending';

    return {
      key: definition.key,
      label: definition.label,
      state,
    };
  });
  const completedStepCount = steps.filter(step => step.state === 'complete').length;

  return {
    steps,
    activeStep: steps[activeStepIndex],
    activeStepNumber: workflow.isComplete
      ? FIRST_COLLECTION_STEP_DEFINITIONS.length
      : activeStepIndex + 1,
    completedStepCount,
    progressPercent: workflow.isComplete ? 100 : clampProgress(workflow.progressPercent),
  };
}

export function getInitialFirstCollectionProgress(): FirstCollectionProgress {
  const steps = FIRST_COLLECTION_STEP_DEFINITIONS.map(
    (definition, index): FirstCollectionStep => ({
      key: definition.key,
      label: definition.label,
      state: index === 0 ? 'active' : 'pending',
    }),
  );

  return {
    steps,
    activeStep: steps[0],
    activeStepNumber: 1,
    completedStepCount: 0,
    progressPercent: 0,
  };
}
